import React, { useState, useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Textarea } from './ui/textarea';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  MessageCircle, 
  X, 
  Send, 
  Loader2, 
  Lock,
  Mail,
  ArrowRight,
  Sparkles
} from 'lucide-react';
import { cn } from '../lib/utils';
import { toast } from 'sonner';

// Locked state component for free users
const LockedState = ({ onClose, onEmailSupport, navigate }) => (
  <div className="flex flex-col items-center justify-center h-full p-6 text-center">
    <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
      <Lock className="w-8 h-8 text-muted-foreground" />
    </div>
    <h3 className="font-heading font-semibold text-lg mb-2">Instant answers, any hour</h3>
    <p className="text-sm text-muted-foreground mb-6">
      AI support chat is included with Standard and Pro — ask anything about invoices, payments, or campaigns and get an answer in seconds.
    </p>
    <Button 
      onClick={() => { onClose(); navigate('/settings'); }}
      className="w-full mb-3"
      data-testid="chat-upgrade-btn"
    >
      Upgrade
      <ArrowRight className="w-4 h-4 ml-2" />
    </Button>
    <Button 
      variant="outline" 
      onClick={onEmailSupport}
      className="w-full"
      data-testid="chat-email-support-locked"
    >
      <Mail className="w-4 h-4 mr-2" />
      Email support
    </Button>
  </div>
);

// Escalate form component
const EscalateForm = ({ userEmail, escalateNote, setEscalateNote, onSubmit, onClose, isEscalating }) => (
  <div className="p-4 space-y-4">
    <div className="flex items-center justify-between">
      <h3 className="font-semibold">Email support</h3>
      <Button variant="ghost" size="sm" onClick={onClose}>
        <X className="w-4 h-4" />
      </Button>
    </div>
    <p className="text-sm text-muted-foreground">
      We&apos;ll reply to {userEmail} — usually within one business day.
    </p>
    <Textarea
      placeholder="Describe your issue (optional)"
      value={escalateNote}
      onChange={(e) => setEscalateNote(e.target.value)}
      rows={4}
      data-testid="escalate-note-input"
    />
    <Button 
      onClick={onSubmit} 
      disabled={isEscalating}
      className="w-full"
      data-testid="escalate-send-btn"
    >
      {isEscalating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
      Send
    </Button>
  </div>
);

const CHAT_SESSION_KEY = 'cc_chat_session_id';
const CHAT_OPEN_KEY = 'cc_chat_open';

const SupportChat = () => {
  const { user, api } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(() => sessionStorage.getItem(CHAT_OPEN_KEY) === '1');
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(() => sessionStorage.getItem(CHAT_SESSION_KEY));
  const [showEscalateForm, setShowEscalateForm] = useState(false);
  const [escalateNote, setEscalateNote] = useState('');
  const [isEscalating, setIsEscalating] = useState(false);
  const [hasShownGreeting, setHasShownGreeting] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const messagesEndRef = useRef(null);
  
  const isPaidUser = user?.subscription_plan && user.subscription_plan !== 'free';
  
  // Greeting message
  const greetingMessage = {
    role: 'assistant',
    content: "Hi! I'm CreateCollect's AI assistant. Ask me anything about invoices, payments, campaigns, or your plan. For billing issues or anything I can't answer, use Email support below and a human will follow up."
  };
  
  // Restore chat history when a stored session exists (survives page navigation)
  useEffect(() => {
    const restoreHistory = async () => {
      if (!sessionId || historyLoaded || !isPaidUser) return;
      setHistoryLoaded(true);
      try {
        const response = await api.get(`/support-chat/session/${sessionId}`);
        const history = response.data.messages || [];
        if (history.length > 0) {
          setMessages([greetingMessage, ...history]);
          setHasShownGreeting(true);
        }
      } catch (error) {
        sessionStorage.removeItem(CHAT_SESSION_KEY);
        setSessionId(null);
      }
    };
    restoreHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, isPaidUser]);
  
  const toggleOpen = (open) => {
    setIsOpen(open);
    sessionStorage.setItem(CHAT_OPEN_KEY, open ? '1' : '0');
  };
  
  useEffect(() => {
    if (isOpen && !hasShownGreeting && isPaidUser && messages.length === 0) {
      setMessages([greetingMessage]);
      setHasShownGreeting(true);
    }
  }, [isOpen, hasShownGreeting, isPaidUser, messages.length]);
  
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);
  
  const handleSend = async () => {
    if (!inputValue.trim() || isLoading) return;
    
    const userMessage = inputValue.trim();
    if (userMessage.length > 1000) {
      toast.error('Message too long (max 1000 characters)');
      return;
    }
    
    setInputValue('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);
    
    try {
      const response = await api.post('/support-chat', {
        message: userMessage,
        session_id: sessionId
      });
      
      setSessionId(response.data.session_id);
      sessionStorage.setItem(CHAT_SESSION_KEY, response.data.session_id);
      setHistoryLoaded(true);
      setMessages(prev => [...prev, { role: 'assistant', content: response.data.response }]);
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      
      if (error.response?.status === 403) {
        // Upgrade required - shouldn't happen for paid users
        toast.error('AI support is available on Standard and Pro plans');
      } else if (error.response?.status === 429) {
        // Daily limit or rate limit
        const message = typeof errorDetail === 'string' 
          ? errorDetail 
          : 'Daily chat limit reached — email us at support@createcollect.com';
        setMessages(prev => [...prev, { role: 'assistant', content: message, isError: true }]);
      } else {
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: 'Something went wrong. Please try again or use Email support below.',
          isError: true 
        }]);
      }
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleEscalate = async () => {
    setIsEscalating(true);
    
    try {
      const response = await api.post('/support-chat/escalate', {
        session_id: sessionId,
        note: escalateNote || null
      });
      
      toast.success(response.data.message);
      setShowEscalateForm(false);
      setEscalateNote('');
    } catch (error) {
      toast.error('Failed to send. Please email support@createcollect.com directly.');
    } finally {
      setIsEscalating(false);
    }
  };
  
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };
  
  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => toggleOpen(true)}
        className={cn(
          "fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-primary text-primary-foreground shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center",
          isOpen && "scale-0 opacity-0"
        )}
        data-testid="support-chat-button"
      >
        <MessageCircle className="w-6 h-6" />
      </button>
      
      {/* Chat panel */}
      {isOpen && (
        <div className="fixed bottom-6 right-6 z-50 w-[380px] max-w-[calc(100vw-48px)] animate-in slide-in-from-bottom-4 duration-200">
          <Card className="shadow-2xl border-2 flex flex-col max-h-[calc(100dvh-96px)]">
            {/* Header */}
            <CardHeader className="pb-3 border-b bg-muted/30 shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <Sparkles className="w-4 h-4 text-primary" />
                  </div>
                  <div>
                    <CardTitle className="text-base font-heading">CreateCollect Assistant</CardTitle>
                    <p className="text-xs text-muted-foreground">AI support — answers in seconds</p>
                  </div>
                </div>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={() => toggleOpen(false)}
                  data-testid="chat-close-btn"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </CardHeader>
            
            <CardContent className="p-0 flex-1 flex flex-col min-h-0">
              {showEscalateForm ? (
                <EscalateForm
                  userEmail={user?.email}
                  escalateNote={escalateNote}
                  setEscalateNote={setEscalateNote}
                  onSubmit={handleEscalate}
                  onClose={() => setShowEscalateForm(false)}
                  isEscalating={isEscalating}
                />
              ) : !isPaidUser ? (
                <LockedState
                  onClose={() => toggleOpen(false)}
                  onEmailSupport={() => setShowEscalateForm(true)}
                  navigate={navigate}
                />
              ) : (
                <>
                  {/* Messages */}
                  <div className="h-[300px] flex-1 min-h-0 overflow-y-auto p-4 space-y-4" data-testid="chat-messages">
                    {messages.map((msg, idx) => (
                      <div 
                        key={idx}
                        className={cn(
                          "flex",
                          msg.role === 'user' ? "justify-end" : "justify-start"
                        )}
                      >
                        <div 
                          className={cn(
                            "max-w-[85%] rounded-2xl px-4 py-2 text-sm",
                            msg.role === 'user' 
                              ? "bg-primary text-primary-foreground rounded-br-sm"
                              : msg.isError
                                ? "bg-destructive/10 text-destructive border border-destructive/20 rounded-bl-sm"
                                : "bg-muted rounded-bl-sm"
                          )}
                          data-testid={`chat-message-${msg.role}`}
                        >
                          {msg.content}
                        </div>
                      </div>
                    ))}
                    {isLoading && (
                      <div className="flex justify-start">
                        <div className="bg-muted rounded-2xl rounded-bl-sm px-4 py-2">
                          <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                        </div>
                      </div>
                    )}
                    <div ref={messagesEndRef} />
                  </div>
                  
                  {/* Input */}
                  <div className="p-4 border-t bg-background">
                    <div className="flex gap-2">
                      <Textarea
                        placeholder="Ask a question..."
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyDown={handleKeyDown}
                        rows={1}
                        className="min-h-[40px] max-h-[100px] resize-none"
                        disabled={isLoading}
                        data-testid="chat-input"
                      />
                      <Button 
                        onClick={handleSend} 
                        disabled={!inputValue.trim() || isLoading}
                        size="icon"
                        data-testid="chat-send-btn"
                      >
                        <Send className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  
                  {/* Email support button - always visible */}
                  <div className="px-4 pb-4">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => setShowEscalateForm(true)}
                      className="w-full text-muted-foreground hover:text-foreground"
                      data-testid="chat-email-support"
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      Email support
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
};

export default SupportChat;
